// (c) chaehongee. Confidential and proprietary.

import type { Dispatch } from '@reduxjs/toolkit'
import {
    clearItems as clearCanvasItems,
    setItems as setCanvasItems,
} from '../../../store/slices/canvasSlice'
import { clearSessionPositions } from '../../../store/slices/canvasPositionsSlice'
import { clear as clearSelection } from '../../../store/slices/selectionSlice'

/**
 * Canvas State Service
 * 
 * Abstracts Redux operations for Canvas component.
 * Provides a clean API that decouples Canvas from Redux implementation details.
 * 
 * Benefits:
 * - Features don't need to know about Redux
 * - Easy to test in isolation
 * - Single responsibility for state management
 * - Easy to swap state implementation in the future
 */
export class CanvasStateService {
    dispatch: Dispatch

    constructor(dispatch: Dispatch) {
        this.dispatch = dispatch
    }

    // Selection Management
    clearSelection(sessionId: string): void {
        this.dispatch(clearSelection({ sessionId }))
    }

    // Canvas Items Management
    setItems(
        sessionId: string,
        items: Array<{ id: string; src: string; mediaType?: string }>
    ): void {
        this.dispatch(setCanvasItems({ sessionId, items }))
    }

    clearItems(sessionId: string): void {
        this.dispatch(clearCanvasItems({ sessionId }))
    }

    // Positions Management
    clearSessionPositions(sessionId: string): void {
        this.dispatch(clearSessionPositions(sessionId))
    }
}

/**
 * Factory function to create CanvasStateService instance
 */
export function createCanvasStateService(dispatch: Dispatch): CanvasStateService {
    return new CanvasStateService(dispatch)
}

